/**
 * A collection of useful components.
 *
 * Logic:
 * - If
 * - IfNot
 * - IfArticlePage
 * - IfNotArticlePage
 * - IfCategoryPage
 * - IfNotCategoryPage
 * - IfHomePage
 * - IfNotHomePage
 * - IfSearchPage
 * - IfNotSearchPage
 * - IfExtraPage
 * - IfNotExtraPage
 * - IfErrorPage
 * - IfNotErrorPage
 *
 * Rendering:
 * - HTML
 *
 * Linking:
 * - HomeLink
 * - ExtraPageLink
 *
 * @module
 */
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { getHomePath, getExtraPagePath } from '../utils/routes';
import { usePageInfo } from '../hooks';
import { getLanguageId } from '../utils/globals';
import RouterLink from '../componentsInternal/RouterLink';
export { Helmet };
/**
 * A simple component to control rendering of a child component(s) depending on a logic test.
 *
 * - Only renders the child content if the test prop is truthy.
 *
 * Usage example:
 * ```javascript
 * import * as Meta from '@elevio/kb-kit/lib/components/meta';
 *
 * const MyIfComponent = () => {
 *   const randomNumber = Math.random();
 *   const test = randomNumber < 0.5;
 *
 *   const message = <strong>I am visible!</strong>;
 *
 *   // message will display only if randomNumber is less than 0.5
 *   return <If test={test}>{message}</If>;
 * };
 * ```
 */
export const If = ({ children, test }) => {
    if (test)
        return React.createElement(React.Fragment, null, children);
    return null;
};
If.displayName = 'Meta.If';
/**
 * A simple component to control rendering of a child component(s) depending on a logic test.
 *
 * - Only renders the child content if the test prop is falsey.
 *
 * Usage example:
 * ```javascript
 * import * as Meta from '@elevio/kb-kit/lib/components/meta';
 *
 * const MyIfNotComponent = () => {
 *   const randomNumber = Math.random();
 *   const test = randomNumber < 0.5;
 *
 *   const message = <strong>I am visible!</strong>;
 *
 *   // message will display if randomNumber is greater than or equal to 0.5
 *   return <IfNot test={test}>{message}</IfNot>;
 * };
 * ```
 */
export const IfNot = ({ children, test }) => {
    if (!test)
        return React.createElement(React.Fragment, null, children);
    return null;
};
IfNot.displayName = 'Meta.IfNot';
/**
 * Pass a string in as children and it will render to unescaped HTML.
 *
 * > **💡 NOTE**: scripts will not be executed, if you need that use the [[InnerHTML]] component
 */
export const HTML = ({ children, ...props }) => {
    return React.createElement("div", { ...props, dangerouslySetInnerHTML: { __html: children } });
};
HTML.displayName = 'Meta.HTML';
/**
 * A component to link automatically back to the root of the Knowledge Base.
 *
 * Usage example:
 * ```javascript
 * import * as Meta from "@elevio/kb-kit/lib/components/Meta";
 *
 * const MyPageWithHomeLink = ({
 *   articleId,
 *   slug,
 *   title,
 * }: HomeLinkProps) => {
 *   const customClass = 'home-link';
 *   return (
 *     <body>
 *       <nav>
 *         <Meta.HomeLink
 *           className={customClass}
 *           articleId={articleId}
 *           slug={slug}
 *           title={title}
 *         >
 *           Home
 *         </Meta.HomeLink>
 *       </nav>
 *       <main>
 *         <h1>My Page</h1>
 *         <p>Welcome!</p>
 *       </main>
 *     </body>
 *   );
 * };
 * ```
 */
export function HomeLink({ children, className, title, ...props }) {
    return (React.createElement(RouterLink, { to: getHomePath({ languageId: getLanguageId() }), className: className, "data-testid": "home-link", title: title, exact: true, ...props }, children));
}
HomeLink.displayName = 'Meta.HomeLink';
/**
 * A link to any extra routes you define.
 *
 * > **💡 NOTE**: The current language will be automatically pre-pended.
 *
 * Usage example:
 * ```javascript
 * import * as Meta from "@elevio/kb-kit/lib/components/Meta";
 *
 * const MyPageWithExtraPageLinkToLoginPortal = () => {
 *   const homeClass = 'home-link';
 *   const loginClass = 'login-link';
 *   return (
 *     <body>
 *       <nav>
 *         <Meta.HomeLink className={homeClass}>Home</Meta.HomeLink>
 *         <Meta.ExtraPageLink path="/login" className={loginClass}>
 *           Log in
 *         </Meta.ExtraPageLink>
 *       </nav>
 *       <main>
 *         <h1>My Page</h1>
 *         <p>Welcome!</p>
 *       </main>
 *     </body>
 *   );
 * };
 * ```
 */
export const ExtraPageLink = ({ children, path, ...props }) => {
    return (React.createElement(RouterLink, { to: getExtraPagePath({ languageId: getLanguageId(), path }), ...props }, children));
};
ExtraPageLink.displayName = 'Meta.ExtraPageLink';
/**
 * Is an article page
 */
export const IfArticlePage = ({ children }) => {
    const info = usePageInfo();
    if (!info)
        return null;
    if (info.pageType === 'article')
        return React.createElement(React.Fragment, null, children);
    return null;
};
IfArticlePage.displayName = 'Meta.IfArticlePage';
/**
 * Is not an article page
 */
export const IfNotArticlePage = ({ children }) => {
    const info = usePageInfo();
    if (!info)
        return null;
    if (info.pageType !== 'article')
        return React.createElement(React.Fragment, null, children);
    return null;
};
IfNotArticlePage.displayName = 'Meta.IfNotArticlePage';
/**
 * Is a category page
 */
export const IfCategoryPage = ({ children }) => {
    const info = usePageInfo();
    if (!info)
        return null;
    if (info.pageType === 'category')
        return React.createElement(React.Fragment, null, children);
    return null;
};
IfCategoryPage.displayName = 'Meta.IfCategoryPage';
/**
 * Is not a category page
 */
export const IfNotCategoryPage = ({ children }) => {
    const info = usePageInfo();
    if (!info)
        return null;
    if (info.pageType !== 'category')
        return React.createElement(React.Fragment, null, children);
    return null;
};
IfNotCategoryPage.displayName = 'Meta.IfNotCategoryPage';
/**
 * Is the home page
 */
export const IfHomePage = ({ children }) => {
    const info = usePageInfo();
    if (!info)
        return null;
    if (info.pageType === 'home')
        return React.createElement(React.Fragment, null, children);
    return null;
};
IfHomePage.displayName = 'Meta.IfHomePage';
/**
 * Is not the home page
 */
export const IfNotHomePage = ({ children }) => {
    const info = usePageInfo();
    if (!info)
        return null;
    if (info.pageType !== 'home')
        return React.createElement(React.Fragment, null, children);
    return null;
};
IfNotHomePage.displayName = 'Meta.IfNotHomePage';
/**
 * Is a search page
 */
export const IfSearchPage = ({ children }) => {
    const info = usePageInfo();
    if (!info)
        return null;
    if (info.pageType === 'search')
        return React.createElement(React.Fragment, null, children);
    return null;
};
IfSearchPage.displayName = 'Meta.IfSearchPage';
/**
 * Is not a search page
 */
export const IfNotSearchPage = ({ children }) => {
    const info = usePageInfo();
    if (!info)
        return null;
    if (info.pageType !== 'search')
        return React.createElement(React.Fragment, null, children);
    return null;
};
IfNotSearchPage.displayName = 'Meta.IfNotSearchPage';
/**
 * Is an extra page
 */
export const IfExtraPage = ({ children }) => {
    const info = usePageInfo();
    if (!info)
        return null;
    if (info.pageType === 'extra')
        return React.createElement(React.Fragment, null, children);
    return null;
};
IfExtraPage.displayName = 'Meta.IfExtraPage';
/**
 * Is not an extra page
 */
export const IfNotExtraPage = ({ children }) => {
    const info = usePageInfo();
    if (!info)
        return null;
    if (info.pageType !== 'extra')
        return React.createElement(React.Fragment, null, children);
    return null;
};
IfNotExtraPage.displayName = 'Meta.IfNotExtraPage';
/**
 * Is an error page
 */
export const IfErrorPage = ({ children }) => {
    const info = usePageInfo();
    if (!info)
        return null;
    if (info.pageType === 'error' ||
        info.pageType === 'error401' ||
        info.pageType === 'error404')
        return React.createElement(React.Fragment, null, children);
    return null;
};
IfErrorPage.displayName = 'Meta.IfErrorPage';
/**
 * Is not an error page
 */
export const IfNotErrorPage = ({ children }) => {
    const info = usePageInfo();
    if (!info)
        return null;
    if (info.pageType !== 'error' &&
        info.pageType !== 'error401' &&
        info.pageType !== 'error404')
        return React.createElement(React.Fragment, null, children);
    return null;
};
IfNotErrorPage.displayName = 'Meta.IfNotErrorPage';
//# sourceMappingURL=meta.js.map